import { type GoogleAnalyticsEventParameterKeys, type GoogleAnalyticsEventParameterValues, GoogleAnalyticsEvents } from "../../util/types/GoogleAnalyticsEvents";
import { environment } from "../../util/env/environment";
import { ICurrentWindowStore } from "../window/ICurrentWindowStore";
import { action, computed, reaction } from "mobx";
import { GoogleAnalyticsInfoModel } from "../../../OpenApi/api";
import { IGoogleAnalyticsEventParameter } from "./Models/IGoogleAnalyticsEventParameter";
import { ICookieSettingsStore } from "../cookie-banner/CookieSettingsStore";

//There is viewer specific logic in this store,
//- shouldSendAction and updateState methods
//in future we might want a separate ViewerLevelGoogleAnalyticsStore, that extends this one
//This requires quite a lot of work and time ...
//which we don't have at the moment, so for now, carry on...

export interface IGoogleAnalyticsStore {
	init(): void;
	setCurrentWindowStore(currentWindowStore: ICurrentWindowStore): void;
	sendGoogleAnalyticsEvent(action: GoogleAnalyticsEvents, parameters?: IGoogleAnalyticsEventParameter[]): void;
}

export class GoogleAnalyticsStore implements IGoogleAnalyticsStore {
	private _hasMouseScrolledAlready: boolean = false;
	private _hasCineScrolledAlready: boolean = false;
	private _hasDragScrolledAlready: boolean = false;
	private _hasArrowScrolledAlready: boolean = false;
	private _hasScrollbarScrolledAlready: boolean = false;
	private _hasViewTiledAlready: boolean = false;

	private readonly measurementId: string = environment == "production" ? "G-TZRK9HFV61" : "G-VSXDBSPZS0";
	private readonly initializedWindows = new WeakSet<Window>();

	constructor(
		private readonly googleAnalyticsInfo: GoogleAnalyticsInfoModel,
		private readonly cookieSettingsStore: ICookieSettingsStore,
		private currentWindowStore: ICurrentWindowStore,
	) {}

	@action
	public init() {
		//(re)trigger loadScript() when shouldLoadScript sets to true (user accepts cookies)
		//or when currentWindow changes (multimonitor)
		reaction(
			() => ({
				shouldLoadScript: this.shouldLoadScript,
				window: this.currentWindowStore.currentWindow,
			}),
			({ shouldLoadScript }) => {
				if (!shouldLoadScript) return;
				this.loadScript();
			},
			{ fireImmediately: true },
		);
	}

	@action
	private loadScript() {
		if (!this.shouldLoadScript) return;
		const win = this.currentWindowStore.currentWindow;
		if (this.initializedWindows.has(win)) return;

		const script = win.document.createElement("script");
		script.async = true;
		script.src = `/POW4/scripts/ga/ga.js`;
		script.type = "text/javascript";
		win.document.head.appendChild(script);

		win.dataLayer = win.dataLayer || [];
		win.gtag = function () {
			win.dataLayer.push(arguments);
		};
		win.gtag("js", new Date());

		//Debug mode = true -> to allow live event viewing in Google Analytics
		win.gtag("config", this.measurementId, {
			debug_mode: true,
			send_page_view: false,
		});

		win.gtag("consent", "default", {
			ad_storage: "denied",
			analytics_storage: "granted",
		});

		this.initializedWindows.add(win);
	}

	@computed
	private get shouldLoadScript(): boolean {
		return this.googleAnalyticsInfo.loadScript && this.cookieSettingsStore.acceptedAllCookies;
	}

	sendGoogleAnalyticsEvent(action: GoogleAnalyticsEvents, parameters: IGoogleAnalyticsEventParameter[] = []): void {
		if (!this.shouldLoadScript) return;
		const win = this.currentWindowStore.currentWindow;
		if (!this.initializedWindows.has(win)) return;
		if (!this.shouldSendAction(action)) return;

		const params: { [key in GoogleAnalyticsEventParameterKeys]: GoogleAnalyticsEventParameterValues } = Object.assign({}, ...parameters.map(p => ({ [p.key]: p.getValue })));

		params["POW_login_type"] = this.googleAnalyticsInfo.loginType;
		params["POW_use_type"] = "Web";

		if (typeof win.electron !== "undefined") {
			params["POW_use_type"] = "Multi monitor app";
		}

		try {
			win.gtag("event", action, params);
		} catch (e) {
			console.error("Error sending google analytics event", e, action, params);
		}

		this.updateState(action);
	}

	@action
	setCurrentWindowStore(currentWindowStore: ICurrentWindowStore) {
		this.currentWindowStore = currentWindowStore;
	}

	private shouldSendAction(action: GoogleAnalyticsEvents): boolean {
		switch (action) {
			case GoogleAnalyticsEvents.ImageScrollScrollbar:
				return !this._hasScrollbarScrolledAlready;
			case GoogleAnalyticsEvents.ImageScrollDrag:
				return !this._hasDragScrolledAlready;
			case GoogleAnalyticsEvents.ImageScrollArrowKeys:
				return !this._hasArrowScrolledAlready;
			case GoogleAnalyticsEvents.ImageScrollMouse:
				return !this._hasMouseScrolledAlready;
			case GoogleAnalyticsEvents.ImageScrollCine:
				return !this._hasCineScrolledAlready;
			case GoogleAnalyticsEvents.ViewTiling:
				return !this._hasViewTiledAlready;
			default:
				return true;
		}
	}

	private updateState(action: GoogleAnalyticsEvents): void {
		switch (action) {
			case GoogleAnalyticsEvents.ImageScrollScrollbar:
				this._hasScrollbarScrolledAlready = true;
				return;
			case GoogleAnalyticsEvents.ImageScrollDrag:
				this._hasDragScrolledAlready = true;
				return;
			case GoogleAnalyticsEvents.ImageScrollArrowKeys:
				this._hasArrowScrolledAlready = true;
				return;
			case GoogleAnalyticsEvents.ImageScrollMouse:
				this._hasMouseScrolledAlready = true;
				return;
			case GoogleAnalyticsEvents.ImageScrollCine:
				this._hasCineScrolledAlready = true;
				return;
			case GoogleAnalyticsEvents.ViewTiling:
				this._hasViewTiledAlready = true;
				return;
			default:
				return;
		}
	}
}
